import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit, Trash2, Save, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const ContactPageManagement = () => {
  const [contactInfos, setContactInfos] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [editingInfo, setEditingInfo] = useState(null);
  const [formData, setFormData] = useState({
    type: 'address',
    title: '',
    content: '',
    link: '',
    order_index: 0,
  });

  useEffect(() => {
    fetchContactInfos();
  }, []);

  const fetchContactInfos = async () => {
    try {
      const response = await fetch('http://localhost/api/pages.php?type=contact', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'İletişim bilgileri yüklenirken hata oluştu');
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setContactInfos(data);
    } catch (error) {
      console.error('Error fetching contact infos:', error);
      toast.error('İletişim bilgileri yüklenirken hata oluştu');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'order_index' ? parseInt(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingInfo
        ? `http://localhost/api/pages.php?type=contact&id=${editingInfo.id}`
        : 'http://localhost/api/pages.php?type=contact';
      
      const method = editingInfo ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'İletişim bilgisi kaydedilirken hata oluştu');
        return;
      }

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      toast.success(editingInfo ? 'İletişim bilgisi güncellendi' : 'İletişim bilgisi eklendi');
      setIsModalOpen(false);
      setEditingInfo(null);
      setFormData({
        type: 'address',
        title: '',
        content: '',
        link: '',
        order_index: 0,
      });
      fetchContactInfos();
    } catch (error) {
      console.error('Error saving contact info:', error);
      toast.error('İletişim bilgisi kaydedilirken hata oluştu');
    }
  };

  const handleEdit = (info) => {
    setEditingInfo(info);
    setFormData({
      type: info.type,
      title: info.title,
      content: info.content,
      link: info.link || '',
      order_index: info.order_index || 0,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;

    try {
      const response = await fetch(`http://localhost/api/pages.php?type=contact&id=${deleteItemId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.text().then(text => text ? JSON.parse(text) : {}).catch(() => ({}));
        toast.error(errorData.error || 'İletişim bilgisi silinirken hata oluştu');
        return;
      }

      toast.success('İletişim bilgisi silindi');
      setIsDeleteModalOpen(false);
      setDeleteItemId(null);
      fetchContactInfos();
    } catch (error) {
      console.error('Error deleting contact info:', error);
      toast.error('İletişim bilgisi silinirken hata oluştu');
    }
  };

  const openAddModal = () => {
    setEditingInfo(null);
    setFormData({
      type: 'address',
      title: '',
      content: '',
      link: '',
      order_index: contactInfos.length,
    });
    setIsModalOpen(true);
  };

  const getIcon = (type) => {
    const icons = {
      address: <MapPin size={20} />,
      phone: <Phone size={20} />,
      email: <Mail size={20} />,
      hours: <Clock size={20} />,
    };
    return icons[type] || <MapPin size={20} />;
  };

  return (
    <AdminLayout
      addButton={
        <Button
          onClick={openAddModal}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white flex items-center gap-2"
        >
          <Plus size={20} />
          Yeni Bilgi
        </Button>
      }
    >
      <div>
        <div className="mt-6">
          {contactInfos.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl">
              <div className="text-6xl mb-4">📞</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Henüz iletişim bilgisi yok</h3>
              <p className="text-gray-600 mb-6">Yeni bir iletişim bilgisi ekleyerek başlayın</p>
              <Button onClick={openAddModal} className="bg-gradient-to-r from-blue-500 to-blue-600">
                İlk Bilgiyi Ekle
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactInfos
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map((info) => (
                  <div
                    key={info.id}
                    className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all flex flex-col"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-[#C0A062] flex items-center justify-center text-white">
                        {getIcon(info.type)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900">{info.title}</h3>
                        <p className="text-sm text-gray-600">{info.content}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-auto">
                      <Button
                        onClick={() => handleEdit(info)}
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-0 flex items-center justify-center gap-1"
                      >
                        <Edit size={16} className="flex-shrink-0" />
                        <span className="truncate">Düzenle</span>
                      </Button>
                      <Button
                        onClick={() => handleDelete(info.id)}
                        variant="destructive"
                        size="sm"
                        className="flex-1 min-w-0 flex items-center justify-center gap-1"
                      >
                        <Trash2 size={16} className="flex-shrink-0" />
                        <span className="truncate">Sil</span>
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingInfo ? 'İletişim Bilgisini Düzenle' : 'Yeni İletişim Bilgisi Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tip *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="address">Adres</option>
                <option value="phone">Telefon</option>
                <option value="email">E-posta</option>
                <option value="hours">Çalışma Saatleri</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Başlık *</label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Adres, Telefon, vb."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">İçerik *</label>
              <Input
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder="İletişim bilgisi içeriği"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Link (Opsiyonel)</label>
              <Input
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                placeholder="tel:+90..., mailto:..., vb."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sıra</label>
              <Input
                type="number"
                name="order_index"
                value={formData.order_index}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
              >
                <Save size={16} className="mr-2" />
                {editingInfo ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
              >
                İptal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Silme Onayı</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">Bu iletişim bilgisini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteItemId(null);
              }}
            >
              İptal
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
            >
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Silme Onayı</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">Bu iletişim bilgisini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeleteItemId(null);
              }}
            >
              İptal
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
            >
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ContactPageManagement;

